import { useEffect } from 'react';
import { GROWIT_DEMO_URL, growitCopy } from '../growitCopy';

function approvedDemoUrl(value: string): string | null {
  if (!value.startsWith('https://') || /[\s\u0000-\u001f\u007f]/u.test(value)) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname && !url.username && !url.password ? value : null;
  } catch {
    return null;
  }
}

/** A demo URL is injectable for the link-state test. Production uses only the
 * approved constant above; the T1 page has no capture or submit path. */
export function GrowItLanding({ demoUrl = GROWIT_DEMO_URL }: { demoUrl?: string }) {
  const linkUrl = approvedDemoUrl(demoUrl);
  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = description?.content;
    document.title = growitCopy.metaTitle;
    if (description) description.content = growitCopy.metaDescription;
    return () => {
      document.title = previousTitle;
      if (description && previousDescription !== undefined) description.content = previousDescription;
    };
  }, []);
  return (
    <main className="min-h-screen overflow-hidden bg-[#f5f4ec] text-[#173c2b]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <header className="flex min-h-24 items-center border-b border-[#173c2b]/15 py-4">
          <img src="/logo.jpg" alt="Up in Farms" className="h-16 w-20 rounded-sm bg-white object-contain p-1 sm:h-20 sm:w-24" />
        </header>

        <div className="grid gap-10 pb-16 pt-10 sm:pt-14 lg:grid-cols-[minmax(0,1.15fr)_minmax(20rem,0.85fr)] lg:gap-16 lg:pb-24 lg:pt-20">
          <div className="relative z-10 max-w-3xl">
            <div aria-hidden="true" className="mb-7 h-1 w-16 rounded-full bg-uif-brown sm:mb-9" />
            <img src="/growit/growit-logo.svg" alt="Grow It" className="mb-6 h-auto w-40 sm:w-[220px]" />
            <h1 className="max-w-2xl text-4xl font-semibold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl">
              {growitCopy.heading}
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[#264d39] sm:text-xl sm:leading-relaxed">
              {growitCopy.paragraphs[0]}
            </p>

            <div className="mt-10 flex flex-col items-start gap-4 sm:mt-12">
              <button
                type="button"
                disabled
                className="min-h-12 w-full cursor-not-allowed rounded-lg bg-uif-green-dark px-6 py-3 text-center text-base font-semibold leading-snug text-white opacity-80 sm:w-auto"
              >
                {growitCopy.reserveLabel}
              </button>
              {linkUrl ? (
                <a
                  href={linkUrl}
                  rel="noopener noreferrer"
                  className="inline-flex min-h-12 items-center rounded-md py-2 text-base font-semibold text-uif-green-dark underline decoration-uif-brown underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-uif-green-dark"
                >
                  {growitCopy.demoLinkLabel}
                </a>
              ) : (
                <p className="flex min-h-12 items-center text-base font-medium text-[#435b46]">
                  {growitCopy.demoFallback}
                </p>
              )}
            </div>
          </div>

          <div className="relative min-h-[19rem] overflow-hidden rounded-[2rem] bg-uif-green-dark p-8 text-white shadow-xl shadow-uif-green-dark/10 sm:min-h-[26rem] lg:min-h-[35rem]">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-30" style={{ backgroundImage: 'repeating-linear-gradient(160deg, transparent 0, transparent 36px, rgba(255,255,255,.3) 37px, transparent 38px)' }} />
            <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full border-[3rem] border-[#8eab73]/80 sm:h-96 sm:w-96" />
            <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-32 h-80 w-80 rounded-full border-[3rem] border-[#d4a574]/80 sm:h-[28rem] sm:w-[28rem]" />
            <div className="relative flex h-full min-h-[15rem] flex-col justify-end sm:min-h-[22rem] lg:min-h-[31rem]">
              <div aria-hidden="true" className="mb-8 h-px w-full bg-white/40" />
              <p className="-mx-4 max-w-md rounded-xl bg-[#214934]/95 p-4 text-lg leading-relaxed text-white sm:text-xl sm:leading-relaxed">
                {growitCopy.paragraphs[1]}
              </p>
            </div>
          </div>
        </div>

        <section className="border-t border-[#173c2b]/15 pb-16 pt-8 sm:pb-20 sm:pt-10">
          <p className="max-w-4xl text-base leading-relaxed text-[#264d39] sm:text-lg sm:leading-relaxed">
            {growitCopy.paragraphs[2]}
          </p>
        </section>
      </div>
    </main>
  );
}
